// import * as Sentry from '@sentry/browser'
import Bowser from 'bowser'
import MobileDetect from 'mobile-detect'
import React, { useEffect, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import 'url-polyfill'
import audio from 'url:./assets/audio_alert.wav'
import ErrorHandler from './ErrorHandler'
import { BrowserContext } from './browser-context'
import './css/main.css'
import { SessionProvider } from './session-context'

import App from './App'
import { getUnwantedEmails, getVisitId, loadWelcomePacket, postMessage, getFormFill, startVoiceCall } from './js/data-providers'
import { Button } from '@material-ui/core'
import { RetellWebClient } from 'retell-client-js-sdk'

const worker = new Worker(new URL('./js/presence-worker.js', import.meta.url))

const retellWebClient = new RetellWebClient()
retellWebClient.on('call_ended', () => {
  console.log('webcall:call_ended')
  postMessage('webcall:call_ended')
})

// When agent starts talking for the utterance
// useful for animation
retellWebClient.on('agent_start_talking', () => {
  console.log('webcall:agent_start_talking')
  postMessage('webcall:agent_start_talking')
})

// When agent is done talking for the utterance
// useful for animation
retellWebClient.on('agent_stop_talking', () => {
  console.log('webcall:agent_stop_talking')
  postMessage('webcall:agent_stop_talking')
})

// Real time pcm audio bytes being played back, in format of Float32Array
// only available when emitRawAudioSamples is true

retellWebClient.on('audio', (audio) => {
  postMessage('webcall:audio', audio)
})


// Update message such as transcript
// You can get transcrit with update.transcript
// Please note that transcript only contains last 5 sentences to avoid the payload being too large
retellWebClient.on('update', (update) => {
  console.log('webcall:update', update)
  postMessage('webcall:update', update)
})

retellWebClient.on('metadata', (metadata) => {
  console.log('retell:metadata', metadata)
  postMessage('webcall:metadata', metadata)
})

retellWebClient.on('error', (error) => {
  console.error('An error occurred:', error)
  // Stop the call
  retellWebClient.stopCall()
})
window.retell_on_call = false

function is_referrer_in_domains (domains) {
  let hostname = window.location.hostname
  const referrer = window.document.referrer
  if (referrer) {
    try {
      hostname = new URL(referrer).hostname
    } catch {
      hostname = window.location.hostname
      console.log('Error parsing referrer (', referrer, ') as URL, using window location instead')
    }
  }
  hostname = hostname.startsWith('www.') ? hostname.substring(4) : hostname
  const whiteListedUrls = [
    'beta.chatfunnels.com',
    'beta.getsignals.ai',
    'app.chatfunnels.com',
    'app.getsignals.ai',
    'local.chatfunnels.dev',
    'mychatfunnels.com']

  if (
    whiteListedUrls.includes(hostname) ||
    hostname.indexOf('.chatfunnels.chat') !== -1 ||
    !domains ||
    domains.length === 0 ||
    domains.includes(hostname) ||
    domains.includes('www.' + hostname)
  ) {
    return true
  }
  console.log('Current domain not allowed to run Signals script', ' allowed: ', domains, ' current: ', hostname)
  return false
}

function Loader () {
  const [myId, setMyId] = useState(null)
  const [welcomePacket, setWelcomePacket] = useState()
  const [hideApp, setHideApp] = useState(false)
  const [visitID, setVisitID] = useState(getVisitId(myId?.packet_code) || '')
  const myIdRef = useRef(null)
  myIdRef.current = myId
  const token = `visitor_${myIdRef.current?.id}_${myIdRef.current?.packet_code}`

  const botTriggerByFlowIdRef = useRef(null)
  const botTriggerByIdRef = useRef(null)
  const startConversationWithUserIdRef = useRef(null)
  const screenRef = useRef(null)
  const scrollCallbackRef = useRef(null)
  const urlChangeCallbackRef = useRef(null)
  const browser = Bowser.getParser(window.navigator.userAgent)

  const isBadIE = browser.satisfies({
    'internet explorer': '<12'
  })

  const [visualViewportSize, setVisualViewportSize] = useState(null)

  const masterToggle = welcomePacket ? true : false

  useEffect(() => {
    window.addEventListener('message', (message) => {
      if (message.source !== window.parent) {
        return
      }
      const data = message.data
      switch (data.type) {
        case 'id':
          if (!visitID) {
            setVisitID(getVisitId(data.packet_code))
          }
          setMyId({ ...data, visit_id: getVisitId(data.packet_code) })
          screenRef.current = data.screen
          break
        case 'page_change':
          urlChangeCallbackRef.current({
            url: data.url,
            page_title: data.page_title,
            plid: data.plid,
            referrer: data.referrer
          })
          setMyId({
            ...myIdRef.current,
            visit_id: getVisitId(data.packet_code),
            page_url: data.url,
            page_title: data.page_title,
            plid: data.plid,
            referrer: data.referrer
          })
          break
        case 'cf_events':
          if (myIdRef.current.supportBot) {
            return
          }

          if (scrollCallbackRef.current && data.attributes.kind === 'scroll') {
            scrollCallbackRef.current(data.attributes.props.sp)
          }
          break
        case 'conversation_by_user_id':
          startConversationWithUserIdRef.current({ userID: data.userID })
          break
        case 'bot_trigger':
          botTriggerByFlowIdRef.current(data.flow_id)
          break

        case 'start_voice_call':
          console.log('start_voice_call', { data, woncall: window.retell_on_call })
          if (window.retell_on_call) {
            retellWebClient.stopCall()
            window.retell_on_call = false
          } else {
            window.retell_on_call = true
            startVoiceCall({
              token,
              chatServiceUrl: myIdRef.current.chatServiceUrl,
              agentID: data.agentID.agentID,
              direct: data.direct,
              variables: data.variables
            }).then((res) => {
              retellWebClient.startCall({
                sampleRate: 16000,
                accessToken: res.data.attributes.access_token,
                emitRawAudioSamples: true
              }).then((call) => {
                console.log('start call response', call)
              })
            })
          }
          break
        case 'bot_trigger_id':
          console.log('triggering ' + data.botID)
          botTriggerByIdRef.current({
            botID: data.botID,
            preview: data.preview || false,
            submissionID: data?.submissionID
          })
          break
        case 'play_sound':
          try {
            var snd = new Audio(audio)
            snd.play()
          } catch (e) {
            console.log('failed to play alert sound')
          }
          break
        case 'visualviewport_size':
          setVisualViewportSize({ height: data.height, offsetTop: data.offsetTop })
          break
        case 'unwanted_emails':
          getUnwantedEmails({
            chatServiceUrl: myIdRef.current.chatServiceUrl,
            token: `visitor_${myIdRef.current.id}_${myIdRef.current.packet_code}`
          })
          break
        case 'form_fill':
          getFormFill({
            chatServiceUrl: myIdRef.current.chatServiceUrl,
            token: `visitor_${myIdRef.current.id}_${myIdRef.current.packet_code}`,
            form_id: data.form_id,
            pageURL: myIdRef.current.page_url,
            pageTitle: myIdRef.current.page_title,
            visitID: myIdRef.current.visit_id
          })
          break
      }
    })
    postMessage('activate')
    postMessage('show')
  }, [])

  useEffect(() => {
    if (myId && !isBadIE) {
      loadWelcomePacket({
        chatServiceUrl: myId.chatServiceUrl,
        token,
        pageURL: myId.page_url,
        packetCode: myId.packet_code,
        referrer: window.document.referrer
      }).then((packet) => {
        if (!packet) {
          console.log('failed to load welcome packet, aborting')
          return
        }
        //Sentry.setUser({ id: welcome_packet.data.id })

        if (packet.page_blocked) {
          return
        }
        if (!is_referrer_in_domains(packet.domains)) {
          return
        }
        if (packet.mobile_disabled) {
          var md = new MobileDetect(window.navigator.userAgent)
          const phone = !!md.phone()
          if (phone) {
            return
          }
        }
        setWelcomePacket(packet)
        if (packet.triggers) {
          postMessage('set_triggers', packet.triggers)
        }

        if (packet.ga_setup) {
          postMessage('ga_setup', packet.ga_setup)
        }
        postMessage('welcome_packet', {
          countryCode: packet.country_code,
          recaptchaV3Key: packet.recaptcha_v3_key,
          recaptchaV2Key: packet.recaptcha_v2_key
        })
      })
    }
  }, [myId, isBadIE])

  if (hideApp) {
    return <></>
  }

  const showApp = myId && welcomePacket && (masterToggle || myId.mode === 'test-drive')

  if (!showApp) {
    return <></>
  }
  const storagePrefix = myId?.prefix || ''
  return (
    <SessionProvider>
      <BrowserContext.Provider value={browser}>
        <ErrorBoundary FallbackComponent={ErrorHandler} onReset={() => { }}>
          <App
            session_id={myId.id}
            storagePrefix={storagePrefix}
            visit_id={myId.visit_id}
            metaAttributes={myId.meta}
            packet_code={myId.packet_code}
            chatServiceUrl={myId.chatServiceUrl}
            wsURL={welcomePacket?.ws_url}
            preventCriteriaPrompts={myId.preventCriteriaPrompts}
            supportBot={myId.supportBot}
            identity={myId.identity}
            page_load_id={myId.plid}
            page_url={myId.page_url}
            referrer={myId.referrer}
            page_title={myId.page_title}
            screenRef={screenRef}
            botTriggerByFlowIdRef={botTriggerByFlowIdRef}
            botTriggerByIdRef={botTriggerByIdRef}
            startConversationWithUserIdRef={startConversationWithUserIdRef}
            scrollCallbackRef={scrollCallbackRef}
            urlHashCallbackRef={urlChangeCallbackRef}
            autoLoadBotId={myId.auto_load_bot_id}
            submissionID={myId.submissionID}
            debug={myId.debug}
            hide_mode={myId.hide_mode}
            welcomePacket={welcomePacket}
            hideApp={() => setHideApp(true)}
            unhideApp={() => setHideApp(false)}
            visualViewportSize={visualViewportSize}
            mode={myId.mode}
            worker={worker}
          />
        </ErrorBoundary>
      </BrowserContext.Provider>
    </SessionProvider>
  )
}

export default Loader
